import { DateTime } from "luxon";

// Bookable categories worth offering as a preference (mirrors ServicesView).
export const MENU_CATEGORY_TYPES = ["category-service-private", "category-service-court", "category-service-class", "category-service-term"];

export const SLOT_STEP_MIN = 30;

export const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
export const DAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday"
};

export const MAX_PERIODS = 2;
export const emptyPeriod = () => ({ from: "", to: "" });
export const blankPerDay = () => DAY_KEYS.reduce((acc, d) => ({ ...acc, [d]: [emptyPeriod()] }), {});

export const minutesToValue = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
export const minutesToLabel = (m) => DateTime.fromObject({ hour: Math.floor(m / 60), minute: m % 60 }).toFormat("h:mm a");
export const valueToLabel = (v) => {
  const [h, m] = v.split(":").map(Number);
  return DateTime.fromObject({ hour: h, minute: m }).toFormat("h:mm a");
};

// timeRanges persist as a flat [{ days, from, to }] list. "weekdays"/"weekend"
// drive the two-scope editor; anything tagged with a single day name means the
// client used the per-day editor, so we rehydrate into that. Per-day is the
// default for a fresh form (no saved ranges) — a client who saved weekday/weekend
// still reopens in that scope.
export function timeRangesToState(timeRanges) {
  const ranges = Array.isArray(timeRanges) ? timeRanges.filter((r) => r && r.from && r.to) : [];
  const perDayDefault = ranges.length === 0 || ranges.some((r) => DAY_KEYS.includes(r.days));

  if (perDayDefault) {
    const perDay = blankPerDay();
    DAY_KEYS.forEach((d) => {
      const forDay = ranges.filter((r) => r.days === d).map((r) => ({ from: r.from, to: r.to }));
      perDay[d] = forDay.length ? forDay : [emptyPeriod()];
    });
    return { useAdvanced: true, perDayPeriods: perDay, weekdayPeriods: [emptyPeriod()], weekendPeriods: [emptyPeriod()] };
  }

  const pick = (scope) => {
    const out = ranges.filter((r) => r.days === scope).map((r) => ({ from: r.from, to: r.to }));
    return out.length ? out : [emptyPeriod()];
  };
  return { useAdvanced: false, perDayPeriods: blankPerDay(), weekdayPeriods: pick("weekdays"), weekendPeriods: pick("weekend") };
}

// Returns the complete ranges for a scope, or null if any period is half-filled.
export function collectPeriods(periods, days) {
  const out = [];
  for (const p of periods) {
    if (!p.from && !p.to) continue;
    if (!p.from || !p.to) return null;
    out.push({ days, from: p.from, to: p.to });
  }
  return out;
}

const SCOPE_LABELS = { weekdays: "Weekdays (Mon–Fri)", weekend: "Weekend (Sat–Sun)", ...DAY_LABELS };
const SCOPE_ORDER = ["weekdays", "weekend", ...DAY_KEYS];

// Collapse the flat timeRanges list into per-scope display lines for the
// read-only summary, e.g. { label: "Weekdays (Mon–Fri)", value: "12:00 PM – 2:00 PM" }.
export function describeTimeRanges(timeRanges) {
  const ranges = Array.isArray(timeRanges) ? timeRanges.filter((r) => r && r.from && r.to) : [];
  return SCOPE_ORDER.map((scope) => {
    const forScope = ranges.filter((r) => r.days === scope);
    if (!forScope.length) return null;
    return { label: SCOPE_LABELS[scope], value: forScope.map((r) => `${valueToLabel(r.from)} – ${valueToLabel(r.to)}`).join(", ") };
  }).filter(Boolean);
}

export function formatBirthDate(value) {
  if (!value) return "";
  const d = DateTime.fromISO(value);
  return d.isValid ? d.toFormat("dd LLL yyyy") : "";
}
